// ===============================================================================
// index.js - SplitMon Application Entry Point (PWA Enabled)
// ===============================================================================
// Location: /var/www/wow/frontend/src/index.js
// Updated: Added PWA service worker registration
// ===============================================================================

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

// ===============================================================================
// RENDER APPLICATION
// ===============================================================================
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// ===============================================================================
// PWA SERVICE WORKER REGISTRATION
// ===============================================================================
// Register the service worker for offline support and PWA features
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });
      
      console.log('✅ [PWA] ServiceWorker registered:', registration.scope);
      
      // =========================================================================
      // Handle Service Worker Updates
      // =========================================================================
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        console.log('🔄 [PWA] New ServiceWorker installing...');
        
        newWorker.addEventListener('statechange', () => {
          switch (newWorker.state) {
            case 'installed':
              if (navigator.serviceWorker.controller) {
                // New update available
                console.log('🆕 [PWA] New content available! Refresh to update.');
                
                // Dispatch custom event for the app to handle
                window.dispatchEvent(new CustomEvent('swUpdateAvailable', {
                  detail: { registration }
                }));
              } else {
                // First install - content cached for offline
                console.log('📦 [PWA] Content cached for offline use');
              }
              break;
              
            case 'redundant':
              console.log('⚠️ [PWA] ServiceWorker became redundant');
              break;
              
            default:
              break;
          }
        });
      });
      
      // =========================================================================
      // Check for Updates Periodically
      // =========================================================================
      // Check for updates every hour
      setInterval(() => {
        registration.update();
        console.log('🔍 [PWA] Checking for updates...');
      }, 60 * 60 * 1000); // 1 hour
      
    } catch (error) {
      console.error('❌ [PWA] ServiceWorker registration failed:', error);
    }
  });
  
  // ===========================================================================
  // Handle Controller Change (New Service Worker Activated)
  // ===========================================================================
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!refreshing) {
      refreshing = true;
      console.log('🔄 [PWA] New ServiceWorker activated, reloading...');
      window.location.reload();
    }
  });
}

// ===============================================================================
// PWA INSTALL PROMPT HANDLER
// ===============================================================================
// Capture the install prompt for custom install button
let deferredPrompt = null;

window.addEventListener('beforeinstallprompt', (e) => {
  // Prevent Chrome's default mini-infobar
  e.preventDefault();
  
  // Store the event for later use
  deferredPrompt = e;
  
  console.log('📱 [PWA] Install prompt captured');
  
  // Dispatch custom event for the app to handle
  window.dispatchEvent(new CustomEvent('pwaInstallAvailable', {
    detail: { prompt: deferredPrompt }
  }));
});

// Track successful installation
window.addEventListener('appinstalled', () => {
  console.log('🎉 [PWA] App installed successfully!');
  deferredPrompt = null;
  
  // Dispatch custom event
  window.dispatchEvent(new CustomEvent('pwaInstalled'));
});

// ===============================================================================
// ONLINE/OFFLINE STATUS HANDLER
// ===============================================================================
// Track network status changes
window.addEventListener('online', () => {
  console.log('🌐 [Network] Back online');
  window.dispatchEvent(new CustomEvent('networkStatusChange', {
    detail: { online: true }
  }));
});

window.addEventListener('offline', () => {
  console.log('📴 [Network] Gone offline');
  window.dispatchEvent(new CustomEvent('networkStatusChange', {
    detail: { online: false }
  }));
});

// ===============================================================================
// EXPORT INSTALL PROMPT FOR COMPONENTS
// ===============================================================================
// Allow components to trigger the install prompt
export const triggerInstallPrompt = async () => {
  if (!deferredPrompt) {
    console.log('❌ [PWA] No install prompt available');
    return { accepted: false, reason: 'no-prompt' };
  }
  
  // Show the install prompt
  deferredPrompt.prompt();
  
  // Wait for user choice
  const { outcome } = await deferredPrompt.userChoice;
  
  console.log(`📱 [PWA] Install prompt ${outcome}`);
  
  // Clear the prompt
  deferredPrompt = null;
  
  return { accepted: outcome === 'accepted', reason: outcome };
};

// Check if already installed
export const isAppInstalled = () => {
  // Check if running in standalone mode (installed PWA)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // Check iOS Safari standalone mode
  if (window.navigator.standalone === true) {
    return true;
  }
  
  return false;
};

// Check if install is available
export const isInstallAvailable = () => {
  return deferredPrompt !== null;
};
